/**
 * Project Stats API service
 * Handles analytics data fetching for the Stats page
 */

import { get } from './api';

const ENDPOINTS = {
  EXTENSION: '/stats/extension',
  CRAWLER: '/stats/crawler',
  CROSS_PROJECT: '/stats/cross-project',
  WORKERS: '/stats/workers',
};

export async function getExtensionStats() {
  return get<ExtensionStatsResponse>(ENDPOINTS.EXTENSION);
}

export async function getCrawlerStats() {
  return get<CrawlerStatsResponse>(ENDPOINTS.CRAWLER);
}

export async function getCrossProjectStats() {
  return get<CrossProjectStatsResponse>(ENDPOINTS.CROSS_PROJECT);
}

export async function getWorkerStats() {
  return get<WorkerStatsResponse>(ENDPOINTS.WORKERS);
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface ExtensionStatsResponse {
  dailyVolume: { date: string; count: number }[];
  clicksPerUser: {
    allTime: { userId: string; name: string; count: number }[];
    last30d: { userId: string; name: string; count: number }[];
  };
  heatmap: { hour: number; dayOfWeek: number; date: string; count: number }[];
  topDomains: { domain: string; count: number }[];
  topOriginDomains: { domain: string; count: number }[];
  perUserSparklines: {
    dates: string[];
    users: { name: string; data: number[] }[];
  };
  uniqueUrlsPerDay: { date: string; count: number }[];
  usersTable: {
    name: string;
    email: string;
    createdAt: string;
    lastLogin?: string;
    totalClicks: number;
  }[];
}

export interface CrawlerStatsResponse {
  jobsByStatus: { status: string; count: number }[];
  jobsOverTime: { month: string; status: string; count: number }[];
  cumulativePlaces: { week: string; count: number; cumulative: number }[];
  placesPerJob: { search_query: string; job_count: number; unique_places: number }[];
  cellCompletionRates: {
    job_id: string;
    job_name: string;
    search_query: string;
    status: string;
    total_cells: number;
    completed_cells: number;
    failed_cells: number;
    completion_pct: number;
  }[];
  duplicateRates: {
    name: string;
    search_query: string;
    job_id: string;
    total_places: number;
    duplicate_places: number;
    duplicate_pct: number;
  }[];
  jobDurations: { label: string; count: number }[];
  proxySuccessRates: {
    proxy_id: string;
    host: string;
    score: number;
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    success_rate: number;
  }[];
  cellFailureReasons: { error: string; count: number }[];
  topCategories: { category: string; count: number }[];
  ratingDistribution: { bucket: number; count: number }[];
  reviewCountDist: { bucket: string; count: number }[];
  ownerVerified: { verified: number; unverified: number; total: number };
  placesByCountry: { country: string; count: number }[];
  priceLevelDist: { level: string; count: number }[];
  dataCompleteness: {
    total: number;
    has_phone: number;
    has_website: number;
    has_hours: number;
    has_images: number;
    has_address: number;
    has_rating: number;
  };
  topSearchQueries: { search_query: string; unique_places: number; job_count: number }[];
}

export interface CrossProjectStatsResponse {
  kpis: {
    totalPlaces: number;
    totalClicks: number;
    activeUsers7d: number;
    coverageRate: number;
    backlog: number;
    completedJobs: number;
    avgDailyClicks30d: number;
    reviewedPlaces: number;
  };
  coverageOverTime: {
    places: { month: string; cumulative: number }[];
    reviewed: { month: string; cumulative: number }[];
  };
  perUserReviews: { name: string; count: number }[];
  jobReviewRates: {
    name: string;
    jobCount: number;
    totalPlaces: number;
    reviewed: number;
    reviewPct: number;
  }[];
  categoryBias: {
    category: string;
    total: number;
    reviewed: number;
    reviewPct: number;
  }[];
  dailyThroughput: {
    scraped: { date: string; count: number }[];
    reviewed: { date: string; count: number }[];
  };
  clickDepth: { depth: number; count: number }[];
}

export interface WorkerStatsResponse {
  workers: {
    userId: string;
    name: string;
    totalClicks: number;
    activeDays: number;
    uniquePlaces: number;
    uniqueUrls: number;
    firstClick: string;
    lastClick: string;
    lastLogin?: string;
    lastSeen?: string;
    lastMapsLogin?: string;
    isOnline?: boolean;
    daysSinceActive: number;
    clicksPerDay: number;
    repeatRatio: number;
    tenure: number;
    avgHoursPerDay?: number;
  }[];
  dailyActivity: {
    dates: string[];
    users: {
      name: string;
      userId: string;
      clicks: number[];
      hours: number[];
      places: number[];
      avgHoursPerDay: number;
    }[];
  };
  heatmaps: {
    name: string;
    userId: string;
    data: { hour: number; dayOfWeek: number; date: string; count: number }[];
  }[];
  domainDistribution: {
    name: string;
    topDomains: { domain: string; count: number }[];
  }[];
  queryCoverage: {
    name: string;
    userId: string;
    queries: { searchQuery: string; reviewed: number; total: number; pct: number }[];
  }[];
  weeklyTrends: {
    weeks: string[];
    users: {
      name: string;
      clicks: number[];
      uniquePlaces: number[];
      activeDays: number[];
    }[];
  };
}
